import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './StoreValue';
import './Login.css';
import { useNavigate } from 'react-router-dom';
import { setSessionStorageWithExpiry } from '../utils/storage';
import { getAgentConfig } from '../utils/agentConfig';

const Login = () => {
    const [loading, setLoading] = useState(false);  
    const [isLogin, setIsLogin] = useState(true); // State to switch between login and signup
    const [loginInfo, setloginInfo] = useState({
        dba: '',
        phone: '',
        email: '',
    });
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const handleChange = (e) => {
        const { name, value } = e.target;
    
        if (name in loginInfo) {
            setloginInfo({
                ...loginInfo,
                [name]: value
            });
        }};
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState(''); 
    const [agentId, setAgentId] = useState(null);
    const [isvId, setIsvId] = useState(null);

    // --- USE CONTEXT HOOKS INSTEAD OF LOCAL STATE ---
    const { agentIdVal, ContactGuid, leadGuid, updateAgentId, updateContactGuid, updateLeadGuid, updateLoginInfo, updateIsvId } = useAuth();

   
    useEffect(() => {
        updateAgentId(null);       // Clears agentIdVal and removes from localStorage
        updateLeadGuid(null);      // Clears leadGuid and removes from localStorage
        updateContactGuid(null);   // Clears ContactGuid and removes from localStorage
        updateLoginInfo({ dba: '', phone: '', email: '' }); // Clears persisted login creds on a new login
        sessionStorage.removeItem('completedSections'); // Clear sidebar progress on new login

        // Ensure window is defined (browser environment)
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            const rawAgentId = urlParams.get('AgentId');
            const rawIsv     = urlParams.get('ISV');
            // AgentId must be 1–6 digits; ISV must be alphanumeric (max 32 chars). Junk → null.
            const agentIdFromUrl = /^\d{1,6}$/.test(rawAgentId ?? '') ? rawAgentId : null;
            const isvIdFromUrl   = /^[A-Za-z0-9_-]{1,32}$/.test(rawIsv ?? '') ? rawIsv : null;
            setAgentId(agentIdFromUrl);
            setIsvId(isvIdFromUrl);
        }
    }, []);

    React.useEffect(() => {
        setSessionStorageWithExpiry("agentId", agentId);
    }, [agentId]);

    const agentConfig = getAgentConfig(agentId);
 
    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrorMessage(''); // Clear previous error messages
        setLoading(true);  //  Start loading
        if (agentId === null) {
            setErrorMessage('Agent ID is required in the URL. Please provide a valid Agent ID.');
            setLoading(false);  // Stop loading after setting error
            return; // Stop form submission
        }
        // Block submission on an invalid phone so it never reaches the backend
        // (mirrors the guard in ClientInfoForm/ContactInfoForm). Recompute digits here
        // rather than relying on the blur-only phoneError, in case the user never blurred.
        const phoneDigits = (loginInfo.phone || '').replace(/\D/g, '');
        if (phoneDigits.length !== 10) {
            setPhoneError('Phone number must contain exactly 10 digits with format with 711-111-1111');
            setLoading(false);
            return;
        }
        // Normalize to the backend's dashed format (xxx-xxx-xxxx) at submit time, so an
        // autofilled/unblurred phone is not sent as raw digits (rejected by PhoneUSType).
        const formattedPhone = formatPhoneNumber(loginInfo.phone);
        if(isvId !== null && agentId !== null)
            {
            // Stash the login values first so they survive into ClientInfo regardless of path.
            updateLoginInfo({ dba: loginInfo.dba, phone: formattedPhone, email: loginInfo.email });
            // Persist the ISV template id so the deferred clone can run at the ClientInfo save
            // (we no longer clone at login — that minted an orphan lead on every landing).
            updateIsvId(isvId);

            // Defect B — resume, else defer. A returning ISV merchant must re-open the application
            // they already started, not get a blank one or a duplicate. Look up an existing lead by
            // DBA+phone+email first; if one exists, resume it. If none exists, fall through to the
            // deferred-new-lead path below — no clone happens at login anymore (see that block).
            try {
                // Match on DBA + phone + EMAIL — all three login credentials. The saved application
                // is the source of truth: resuming requires the exact credentials it was started
                // with. TRX filters on Contact.Email (verified), so a different email yields X7 (no
                // match) and falls through to creating a new application rather than opening someone
                // else's / the wrong lead.
                const resumeQuery = {
                    leadguidVal: '',
                    agentId: agentId,
                    Detail: { TranType: "TranType", TranAction: "TranAction", Ip: 'Ip' },
                    QueryRequest: {
                        Detail: { Active: "1" },
                        ClientInfo: { Dba: loginInfo.dba, Phone: formattedPhone },
                        Contact: { Phone: formattedPhone, Email: loginInfo.email },
                        Corporate: { Phone: formattedPhone }
                    }
                };
                const resumeResp = await axios.post(`${API_BASE_URL}/api/Auth/LeadQuery`, resumeQuery);
                if (resumeResp.data?.Response?.Result?.ResponseCode === '00') {
                    const rows = resumeResp.data?.Response?.QueryResponse?.Row;
                    const row = Array.isArray(rows) ? rows[0] : rows;
                    const foundLeadGuid = row?.Guid ?? '';
                    if (foundLeadGuid) {
                        updateLeadGuid(foundLeadGuid);
                        // Use the URL agent id (not the lead's Client, which for an ISV lead is the
                        // template's client id, e.g. 2531). The agent id drives agent-specific config
                        // such as PromoTix's Pricing-after-Client step order and MCC list; using the
                        // template client would break that. LeadLookup still resolves the lead by guid.
                        updateAgentId(agentId);
                        // Mark as an ISV lead (same sentinel a fresh clone uses): this routes the
                        // next ClientInfo save to the Lead/Update path. A null guid would instead
                        // route it to Lead/Append, which TRX rejects for a request carrying
                        // ClientInfo. ClientInfoForm overwrites this with the real contact guid
                        // when the resumed lead already has an Owner contact.
                        updateContactGuid("ISV_Flag");
                        setLoading(false);
                        navigate('/ClientInfo');
                        return;
                    }
                }
                // No existing lead (X7 / no rows) → fall through to the new-application path below.
            } catch (resumeError) {
                console.error('ISV resume lookup failed; proceeding to create a new lead:', resumeError);
                // Non-fatal: fall through to the new-application path below.
            }

            // No existing lead -> DO NOT clone here. The template clone is deferred to the first
            // ClientInfo save (ClientInfoForm), so a merchant who lands on this app and leaves
            // without saving never creates an orphan lead (the behaviour that was piling up
            // duplicate blank ISV leads). Set up new-lead state: mark it ISV ("ISV_Flag"), keep
            // the URL agent id, and clear any stale leadGuid; the save clones the ISV template
            // once and then writes ClientInfo + the Owner contact against the new lead.
            updateAgentId(agentId);
            updateContactGuid("ISV_Flag");
            updateLeadGuid(null);
            setLoading(false);
            navigate('/ClientInfo');
            return;
                }
              else 
              {
        const data = {
            leadguidVal: leadGuid,
            agentId: agentId,
                Detail: {
                    TranType: "TranType",
                    TranAction: "TranAction",
                    Ip: 'Ip'
                },
                QueryRequest: {
                    Detail: {
                        Active: "1"
                    },
                    ClientInfo: {
                        Dba: loginInfo.dba,
                        Phone: formattedPhone
                    },
                    // Require the email too: resuming a saved application needs the exact DBA +
                    // phone + email it was started with. TRX filters on Contact.Email, so a
                    // different email returns no match and a fresh application is started.
                    Contact: {
                        Phone: formattedPhone,
                        Email: loginInfo.email
                    },
                    Corporate: {
                        Phone: formattedPhone
                    }
                }
           };
        try 
       {
            updateLoginInfo({ dba: loginInfo.dba, phone: formattedPhone, email: loginInfo.email });
            // Non-ISV path: clear any stale ISV template id so it can never trigger a deferred
            // clone for a non-ISV merchant on the ClientInfo save.
            updateIsvId(null);
            const response = await axios.post(`${API_BASE_URL}/api/Auth/LeadQuery`, data);
            var responseCode = response.data.Response.Result.ResponseCode;
            var errorInfo = response.data.Response.Result.ErrorInfo;
            if (responseCode === '00') 
            {
                
             const responseData = response.data; // Alias for brevity

const queryResponse = responseData.Response?.QueryResponse;
const rows = queryResponse?.Row;

let receivedLeadGuid1 = '';
let receivedLeadGuid2 = '';

// Check if 'rows' is an array and it's not empty
if (Array.isArray(rows) && rows.length > 0) {
    const leadRow = rows[0]; // Get the first object in the 'Row' array
    if(leadRow!==null)
    {
    // Safely get Guid and Client from the leadRow object
    receivedLeadGuid1 = leadRow.Guid ?? '';    
    receivedLeadGuid2 = leadRow.Client ?? '';  
} 
}
if (!Array.isArray(rows)) {
     receivedLeadGuid1 = rows.Guid ?? '';    
    receivedLeadGuid2 = rows.Client ?? ''; 
}

updateLeadGuid(receivedLeadGuid1);
updateAgentId(receivedLeadGuid2); // Assuming updateAgentId is meant for 'Client' here.
                    // --- STORE THEM IN THE CONTEXT ---
                    
                navigate('/ClientInfo');
            }
            else if(responseCode === 'X7') 
            {
                updateAgentId(agentId);
                navigate('/ClientInfo');
            }
            else {
                setErrorMessage(errorInfo);
            }
        
        } catch (error) {
            console.error('Error submitting login:', error);
            const serverMsg = error.response?.data?.message || error.response?.data || error.message;
            setErrorMessage(typeof serverMsg === 'string' ? serverMsg : 'An error occurred during login.');
        } finally {
            setLoading(false);  // SStop loading after response
        }
    }
    };
     const [phoneError, setPhoneError] = useState('');
    
        // Phone number formatting function
        const formatPhoneNumber = (phone) => {
            // Remove all non-digit characters
            const digits = phone.replace(/\D/g, '');
            
            // Format phone number with dashes if it has 10 digits
            if (digits.length === 10) {
                return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
            }
            return phone; // Return as is if not 10 digits
        };
    const handlePhoneBlur = () => {
        const formattedPhone = formatPhoneNumber(loginInfo.phone);
        setloginInfo({ ...loginInfo, phone: formattedPhone });

        // Validate if phone number has 10 digits
        const digits = loginInfo.phone.replace(/\D/g, '');
        if (digits.length !== 10) {
            setPhoneError('Phone number must contain exactly 10 digits with format with 711-111-1111');
        } else {
            setPhoneError('');
        }
    };

    const { logo: logo1, logoClass } = agentConfig;

    return (
        <div className="auth-conta">
           <img src={logo1} alt="Company Logo" className={`Loginlogo${logoClass ? ' ' + logoClass : ''}`} />

           <div className="auth-container">
            {loading && (
                <div className="loading-overlay">
                <div className="spinner-border" role="status">
                </div>
                </div>
        )}
    
           <div className="layoutLogin">
            {/* Tabs for Login and Signup */}
            <div className="auth-tabs">
              <button className={isLogin ? "active" : ""} onClick={() => setIsLogin(true)}>
                Login
              </button>
              <button className={!isLogin ? "active" : ""} onClick={() => setIsLogin(false)}>
                Signup
              </button>
            </div>
    
            {/* Display error message if agentId is null */}
            {errorMessage && <p className="error-message">{errorMessage}</p>}
    
            {/* Form Container */}
            <form onSubmit={handleSubmit} className="auth-form">
              <label>
                <input type="text" name="dba" placeholder='DBA Name' value={loginInfo.dba} onChange={handleChange} required />
              </label>
              <label>
                <input type="email" name="email" placeholder='Email' value={loginInfo.email} onChange={handleChange} required />
              </label>
              <label>
                <input type="text" name="phone" placeholder='Phone' value={loginInfo.phone} onChange={handleChange} onBlur={handlePhoneBlur} required />
                {phoneError && <p className="error-message">{phoneError}</p>}
              </label>
              <button type="submit" className="submit-btn">{isLogin ? "Login" : "Signup"}</button>
              
            </form>
        </div>
        </div>
        <div><p className='auth-cont'>Copyright &copy; {new Date().getFullYear()} All Rights Reserved.</p></div>
        </div>
      );
    };
    
    export default Login;
